/**
 * Syncs one in-flight assistant turn into the chat when `thinkingChevron` is on.
 *
 * The flag-off path in `useExecutionMessageSync` splits a turn into a separate
 * chat bubble at every system-event boundary. With chevrons the whole turn is a
 * single message carrying an ordered text + tool-call timeline, so tool activity
 * renders inline between text runs instead of fragmenting the conversation.
 *
 * Extracted from the hook to keep that effect readable: the two strategies are
 * independent, and only one runs per render.
 */

import { Message } from "@/types";
import { getUserFriendlyErrorMessage } from "@/utils/errorMessages";
import { chatItemsToText } from "@/utils/toolChevrons";
import {
  ExecutionStatus,
  ExecutionState,
  isExecutionActive,
  resetExecution,
} from "../../../../redux/reducers/Execution";
import { AppDispatch } from "../../../../redux/store";
import { ChatStreamingRefs } from "./useChatStreamingRefs";

/** Shown when the backend rejects a request as already in flight. */
const DUPLICATE_REQUEST_MESSAGE =
  "A request is already being processed for this project. Please wait for it to complete.";

interface SyncChevronTurnOptions {
  executionState: ExecutionState;
  conversationId: string;
  refs: ChatStreamingRefs;
  addMessage: (conversationId: string, message: Omit<Message, "id">) => string;
  updateMessage: (
    conversationId: string,
    messageId: string,
    updates: Partial<Message>,
  ) => void;
  dispatch: AppDispatch;
}

export function syncChevronTurn({
  executionState,
  conversationId,
  refs,
  addMessage,
  updateMessage,
  dispatch,
}: SyncChevronTurnOptions): void {
  const { status, streamingChatItems, errorCode, errorMessage } =
    executionState;
  const { streamingMessageIdRef, processedSegmentsRef } = refs;

  if (streamingChatItems.length > 0) {
    let msgId = streamingMessageIdRef.current;
    if (!msgId && isExecutionActive(status)) {
      msgId = addMessage(conversationId, {
        content: "",
        isUser: false,
        isLoading: true,
      });
      streamingMessageIdRef.current = msgId;
    }
    if (msgId) {
      updateMessage(conversationId, msgId, {
        content: chatItemsToText(streamingChatItems),
        chatItems: streamingChatItems,
        isLoading: isExecutionActive(status),
      });
    }
  }

  const chevronMsgId = streamingMessageIdRef.current;

  if (status === ExecutionStatus.COMPLETED) {
    if (chevronMsgId) {
      updateMessage(conversationId, chevronMsgId, { isLoading: false });
    }
    streamingMessageIdRef.current = null;
    processedSegmentsRef.current = 0;
  }

  // Terminal error states replace the turn with an explanation. `chatItems` must
  // be cleared, not just overwritten alongside `content`: while it is present
  // AIResponse renders the chevron timeline and reads its trailing text instead
  // of `content`, so the error would be invisible behind a stale half-finished
  // turn (tool calls frozen mid-"running").
  if (status === ExecutionStatus.FAILED) {
    if (chevronMsgId) {
      updateMessage(conversationId, chevronMsgId, {
        content: getUserFriendlyErrorMessage(errorCode, errorMessage),
        chatItems: undefined,
        isLoading: false,
      });
    }
    streamingMessageIdRef.current = null;
    processedSegmentsRef.current = 0;
    dispatch(resetExecution());
  }

  if (status === ExecutionStatus.DUPLICATE) {
    if (chevronMsgId) {
      updateMessage(conversationId, chevronMsgId, {
        content: DUPLICATE_REQUEST_MESSAGE,
        chatItems: undefined,
        isLoading: false,
      });
    }
    streamingMessageIdRef.current = null;
    processedSegmentsRef.current = 0;
    dispatch(resetExecution());
  }

  // Safety net for a missed COMPLETED transition (React 18 batching).
  if (status === ExecutionStatus.IDLE && streamingMessageIdRef.current) {
    updateMessage(conversationId, streamingMessageIdRef.current, {
      isLoading: false,
    });
    streamingMessageIdRef.current = null;
    processedSegmentsRef.current = 0;
  }
}
